import { Component, ElementRef, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { filter } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { deepCloneArray } from '@miq/catalog';
import { OverlayService } from '@miq/stacking-plan';
import { EMPTY_STRING } from '@miq/utils';
import { isCommandAction } from '@shared/components/tree-data-table/commands/commands.model';

import { LookupRefService } from '@shared/services/lookup-ref.service';
import { ComplexTableComponent } from '../complex-table.component';
import { ColumnComplexTable, HandleType, OverlayObject } from '../complex-table.const';
import { Position, RowChanges, SingleChange, SortMode } from '../complex-table.model';
import { UserInfoResolver } from '@shared/resolvers/user-info.resolver';
import { BehaviorSubject } from 'rxjs';
import { HttpParams } from '@angular/common/http';

export const DO_NO_CLOSE_ON_CLICK_CLASS = 'on-click-keep-inline-edition';

@UntilDestroy()
@Component({
  /* tslint:disable */
  selector: '[miq-complex-table-row]',
  /* tslint:enable */
  templateUrl: './complex-table-row.component.html',
  styleUrls: ['./complex-table-row.component.scss'],
  standalone: false,
})
export class ComplexTableRowComponent implements OnInit, OnDestroy {
  public _columns: ColumnComplexTable[];
  @Input() row: Record<string, any>;
  @Input()
  set columns(cols: ColumnComplexTable[]) {
    this._columns = deepCloneArray(cols) as ColumnComplexTable[];
    this.columnHandlerProcess();
  }
  get columns(): ColumnComplexTable[] {
    return this._columns as ColumnComplexTable[];
  }

  @Input() expandable = true;
  @Input() sortable = false;
  @Input() sortMode: SortMode = SortMode.Default;
  @Input() sortMessage = EMPTY_STRING;
  @Input() selectable = true;
  @Input() disableSelection = false;
  @Input() expandedMessage = 'recordsSearch.hideDetails';
  @Input() nonExpandedMessage = 'recordsSearch.showDetails';
  @Input() showGoToRecordColumn = false;
  @Input() rowLinkGenerator: (x: Record<string, any>) => string[] = null;

  @Output() toggle = new EventEmitter<any>();
  @Output() rowChange = new EventEmitter<SingleChange | RowChanges>();
  @Output() clearSelection = new EventEmitter<boolean>();

  Position = Position;
  SortMode = SortMode;

  private _rowChanges: any = {};
  private _hasEDPWriteAccess = new BehaviorSubject<boolean>(false);
  public hasEDPWriteAccess$ = this._hasEDPWriteAccess.asObservable();

  constructor(
    private eRef: ElementRef,
    private complexTableComponent: ComplexTableComponent,
    private overlayService: OverlayService,
    private lookupRefService: LookupRefService,
    private userInfoResolver: UserInfoResolver
  ) {}
  onChange({ column, value }) {
    /** Removed this condition coz value is not getting added to the changes and there is an existing bug where
     * user edit a row and do the change (EX: select a dropdown value ownership type: Condo)
     * click on Done
     * edit the row again and make the change from Condo to Free Simple and Back to Condo
     * click on Done
     * Expectation: ownership type should be Condo
     * Actual: ownership type is showing as Free Simple
     * */
    this._rowChanges = { ...this._rowChanges, [column.field]: { value, column } };
    this.rowChange.emit({ singleChange: { [column.field]: { value, column } } });
  }
  colClickEvent(col: ColumnComplexTable, row: Record<string, any>, event: MouseEvent) {
    const { handlerType } = col.clickHandler;
    if (handlerType === HandleType.OVERLAY_TYPE) {
      const overlayObject: OverlayObject = {
        event: event,
        row: row,
        overlayService: this.overlayService,
      };
      col.clickHandler.handle(overlayObject);
    } else if (handlerType === HandleType.HANDLE_TYPE) {
      col.clickHandler.handle(row);
    }
  }

  ngOnInit() {
    const isTrustedUser = this.userInfoResolver.isATrustedUser();
    this.userInfoResolver.hasEDPWriteAccess().then(hasEDPWriteAccess => {
      this._hasEDPWriteAccess.next(hasEDPWriteAccess && isTrustedUser);
    });
    this.columns = this.columns.map((col: ColumnComplexTable) => {
      if (col.unit) {
        const field = col.unit.field.split('/');
        col.unit.field = field[field.length - 1];
      }
      return col;
    });
    this.complexTableComponent.tableStateService.saveRow$
      .pipe(
        untilDestroyed(this),
        filter(row => row === this.row)
      )
      .subscribe(() => {
        this.clearSelection.emit(true);
        this.emitRowChanges();
      });
    this.getLovValuesFromCascade();

    this.complexTableComponent.tableStateService.cancelEditRow$.pipe(untilDestroyed(this)).subscribe(() => {
      this._rowChanges = {};
      this.row.editMode = false;
      this.clearSelection.emit(true);
    });
  }

  @HostListener('keydown.enter', ['$event'])
  detectSubmit($event) {
    if (this.eRef.nativeElement.contains($event.target)) {
      this.clearSelection.emit(true);
      this.emitRowChanges();
    }
  }

  @HostListener('document:keydown.escape', ['$event'])
  detectEscapeSubmit($event) {
    if (this.row.editMode) {
      this.clearSelection.emit(true);
      this._rowChanges = {};
      this.emitRowChanges();
      $event.preventDefault();
      $event.stopPropagation();
    }
  }

  /**
   * Validate is the click was part of a element that uses a custom class to identify it and to keep editing
   * for example Datepickers
   * @param event
   */
  #isClickOutSideTheTableCustomValidations = event => {
    const eventPath = event?.path ?? event.composedPath();

    // Used specially for datepicker but it could be helpful for other cases
    // datepicker: If the click is triggered from the modal/calendar of a datepicker
    const isNotConfiguredToKeepEditing = eventPath.some(node => node.classList?.contains(DO_NO_CLOSE_ON_CLICK_CLASS));

    // The datepicker modal/calendar is renderer inside of the cdk-overlay-backdrop
    // so in here it validates if the click is parent of an element with the class used as a flag to close the inline edition
    const checkChildNodes = nodeList =>
      !!Array.from(nodeList).find(
        (child: any) =>
          child.classList?.contains(DO_NO_CLOSE_ON_CLICK_CLASS) ||
          (child.childNodes && checkChildNodes(child.childNodes))
      );
    const isParentOfValidDatePicker =
      event.target?.classList?.contains(DO_NO_CLOSE_ON_CLICK_CLASS) ||
      checkChildNodes(event.target?.nextSibling?.children ?? []);

    return !isNotConfiguredToKeepEditing && !isParentOfValidDatePicker;
  };

  @HostListener('document:click', ['$event'])
  clickedOutside($event) {
    if (
      this.row.editMode &&
      !this.eRef.nativeElement.contains($event.target) &&
      $event.target.tagName !== 'BODY' &&
      !$event.target.classList.contains('mat-option-text') &&
      !isCommandAction($event.target) &&
      this.#isClickOutSideTheTableCustomValidations($event)
    ) {
      this.clearSelection.emit(true);
      this.row.editMode = false;
      if (Object.entries(this._rowChanges).length) {
        this.emitRowChanges();
      }
      $event.preventDefault();
      $event.stopPropagation();
    }
  }

  private emitRowChanges() {
    this.row.editMode = false;
    const rowChange = new RowChanges({ rowChange: this._rowChanges, rowData: { ...this.row } });
    this.row = Object.assign(this.row, rowChange.mergedChanges);
    this.rowChange.emit(rowChange);
  }

  private columnHandlerProcess() {
    this._columns = this._columns.map((column: ColumnComplexTable) => {
      const { clickHandler } = column;
      if (clickHandler) {
        const showHandler = clickHandler.condition ? clickHandler.condition(this.row, this._columns, column) : true;
        column.showHandler = showHandler;
      }
      return column;
    });
  }

  //some columns LOV depends on other column's value
  private getLovValuesFromCascade() {
    const listeningColumns = this._columns.filter(col => col.subscribeOn != null);
    listeningColumns.forEach(col => {
      const { targetColumnId } = col.subscribeOn;
      const dependantColumnField = this._columns.find(column => column.columnId === targetColumnId)?.field;

      const isPostCall = col.lookupCategory.includes('POST:');
      const value = this.row[dependantColumnField];

      let lovUri = col.lookupCategory;

      if (isPostCall && value != null) {
        const params = new HttpParams({ fromObject: { for: value } });
        const concat = lovUri.includes('?') ? '&' : '?';
        lovUri = `${lovUri}${concat}${params.toString()}`;
      }

      this.lookupRefService.loadDataFor(lovUri, false, false, [this.row[dependantColumnField]]).subscribe(values => {
        this.columns = this.columns.map(column => (column.columnId === col.columnId ? { ...column, values } : column));
      });
    });
  }

  ngOnDestroy() {
    /** label translation purpose added */
  }
}
